import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getWithTokenAPI,
  postWithTokenAPI,
  putWithTokenAPI,
} from "../../Util/Api";
import { FaCheck, FaTimes, FaSave, FaUndo } from "react-icons/fa";
import "../../Style/Pages/Role.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { displayName } from "react-quill";
const moduleDisplay = {
  dashboard: { displayName: "Dashboard" },
  drivermanagement: { displayName: "Driver Management" },
  customermanagement: { displayName: "Customer Management" },
  ridemanagement: { displayName: "Ride Management" },
  approval: { displayName: "Approval" },
  prices: { displayName: "Prices" },
  message: { displayName: "Messages" },
  wallet: { displayName: "Wallet" },
  splash_screen: { displayName: "Splash Screen" },
  account: { displayName: "Account" },
  resouragemangement: { displayName: "Resource" },
  rolemanagement: { displayName: "Roles" },
  helpandsupport: { displayName: "Support" },
  logoandtermsCondition: { displayName: "Logo & Terms Condition" },
  location: { displayName: "Location" },
  versioncontrol: { displayName: "Version Control" },
};

const RoleCreate = () => {
  const auth = useSelector((state) => state.auth);
  const token = auth.token;
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const initialPermission = {
    dashboard: { view: false, create: null, update: null, delete: null },
    drivermanagement: {
      view: false,
      create: null,
      update: false,
      delete: null,
    },
    customermanagement: {
      view: false,
      create: null,
      update: false,
      delete: null,
    },
    ridemanagement: { view: false, create: null, update: null, delete: null },
    approval: { view: false, create: null, update: false, delete: false },
    prices: { view: false, create: false, update: false, delete: false },
    message: { view: false, create: false, update: false, delete: null },
    wallet: { view: false, create: null, update: null, delete: null },
    splash_screen: { view: false, create: false, update: false, delete: false },
    account: { view: false, create: null, update: false, delete: null },
    resourcemanagement: {
      view: false,
      create: false,
      update: false,
      delete: false,
    },
    rolemanagement: {
      view: false,
      create: false,
      update: false,
      delete: false,
    },
    helpandsupport: {
      view: false,
      create: null,
      update: false,
      delete: null,
    },
    logoandtermsCondition: {
      view: false,
      create: null,
      update: false,
      delete: null,
    },
    location: {
      view: false,
      create: false,
      update: false,
      delete: false,
    },
    versioncontrol: {
      view: false,
      create: null,
      update: false,
      delete: null,
    },
  };

  const [roleData, setRoleData] = useState({
    title: "",
    description: isEditMode ? "" : "New role created via UI",
    permission: initialPermission,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // 1. Fetch Data for Edit Mode
  useEffect(() => {
    const fetchRoleDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res = await getWithTokenAPI(token, `/role/view/${id}`);
        const data = res?.data?.data || res?.data;

        if (data) {
          setRoleData({
            title: data.title || "",
            description: data.description || "",
            // Merge backend data with initial structure to keep 'null' values for graying out
            permission: {
              ...initialPermission,
              ...(data.permission || {}),
            },
          });
        }
      } catch (err) {
        setError("Failed to fetch role details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRoleDetails();
  }, [id, token]);

  // 2. Handle Individual Checkbox Change
  const handlePermissionChange = (moduleKey, field) => {
    setRoleData((prev) => ({
      ...prev,
      permission: {
        ...prev.permission,
        [moduleKey]: {
          ...prev.permission[moduleKey],
          [field]: !prev.permission[moduleKey][field],
        },
      },
    }));
  };

  // 3. Handle Select All for a Row
  const handleSelectAll = (moduleKey) => {
    setRoleData((prev) => {
      const current = prev.permission[moduleKey];
      const newValue = !current.view; // Toggle based on 'view' status

      return {
        ...prev,
        permission: {
          ...prev.permission,
          [moduleKey]: {
            view: current.view !== null ? newValue : null,
            create: current.create !== null ? newValue : null,
            update: current.update !== null ? newValue : null,
            delete: current.delete !== null ? newValue : null,
          },
        },
      };
    });
  };

  // 4. Helper for Select All State
  const isAllSelected = (moduleKey) => {
    const perm = roleData.permission[moduleKey];
    if (!perm) return false;
    // Check if every non-null field is true
    const relevantKeys = Object.keys(perm).filter((k) => perm[k] !== null);
    return (
      relevantKeys.length > 0 && relevantKeys.every((k) => perm[k] === true)
    );
  };

  const isDisabled = (val) => val === null;

  const handleReset = () => {
    setRoleData({
      title: "",
      description: "New role created via UI",
      permission: initialPermission,
    });
    setError("");
    setSuccess(false);
  };

  // 5. Handle Submit (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roleData.title.trim()) {
      setError("Role name is required");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const url = isEditMode ? `/role/update/${id}` : "/role/create";

      let response;

      if (isEditMode) {
        response = await putWithTokenAPI(token, url, roleData); // ✅ EDIT → PUT
      } else {
        response = await postWithTokenAPI(token, url, roleData); // ✅ CREATE → POST
      }

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        setSuccess(true);
        toast.success(
          isEditMode
            ? "Role updated successfully"
            : "Role created successfully",
        );
        setTimeout(() => navigate("/admin/roles"), 1500);
      } else {
        setError(response?.data?.message || "Failed to save role");
        toast.error(response?.data?.message || "Failed to save role");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Error saving role");
      toast.error("Something went wrong while saving role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="role-create-container">
      <div className="role-create-header">
        <div className="header-with-back">
          <button
            type="button"
            className="back-button"
            onClick={() => navigate("/admin/roles")}
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="role-create-title">
            {isEditMode ? "Edit Role" : "Create New Role"}
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="role-create-form">
        <div className="role-basic-info-section">
          <div className="form-group">
            <label className="form-label">
              Role Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={roleData.title}
              onChange={(e) =>
                setRoleData({ ...roleData, title: e.target.value })
              }
              placeholder="Enter Your Role"
            />
          </div>
          {/* <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows="2"
              value={roleData.description}
              onChange={(e) => setRoleData({ ...roleData, description: e.target.value })}
            />
          </div> */}
        </div>

        <div className="permissions-table-wrapper">
          <table className="permissions-table">
            <thead>
              <tr>
                <th className="permissions-th">Module</th>
                <th className="permissions-th">View</th>
                <th className="permissions-th">Create</th>
                <th className="permissions-th">Update</th>
                <th className="permissions-th">Delete</th>
                <th className="permissions-th">Select All</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(moduleDisplay).map(([moduleKey, module]) => {
                const perm = roleData.permission[moduleKey];
                if (!perm) return null; // Safety check

                return (
                  <tr key={moduleKey} className="permission-row">
                    <td className="permissions-td">
                      <strong>{module.displayName}</strong>
                    </td>

                    {["view", "create", "update", "delete"].map((action) => (
                      <td key={action} className="permissions-td checkbox-cell">
                        <label
                          className={`checkbox-container ${isDisabled(perm[action]) ? "disabled-wrapper" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={perm[action] === true}
                            disabled={isDisabled(perm[action])}
                            onChange={() =>
                              handlePermissionChange(moduleKey, action)
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      </td>
                    ))}

                    <td className="permissions-td checkbox-cell">
                      <label className="checkbox-container select-all">
                        <input
                          type="checkbox"
                          checked={isAllSelected(moduleKey)}
                          onChange={() => handleSelectAll(moduleKey)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={loading}
          >
            <FaUndo /> Reset
          </button>
          <button type="submit" className="btn btn-primary create-buttton-role" disabled={loading}>
            {loading ? (
              <span className="loading-spinner-small"></span>
            ) : (
              <FaSave />
            )}
            {isEditMode ? " Update Role" : " Create Role"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoleCreate;
